import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import {
  AnimationIcon,
  CheckIcon,
  CloseIcon,
  DefaultsIcon,
  ExportIcon,
  GroupsIcon,
  HelpIcon,
  HistoryIcon,
  ImportIcon,
  MoveIcon,
  ProjectIcon,
  RelationshipIcon,
  ResetZoomIcon,
  ShortcutsIcon,
  TableIcon,
} from '@/components/ui/icons'
import { useModalStore } from '@/hooks/useModalStore'
import { useTableEditorModal } from '@/hooks/useTableEditorModal'
import { useSelectionStore } from '@/stores'
import { morphToast } from '@/components/ui/Toast'
import { markCanvasGuideSeen } from '@/utils/canvasGuideStorage'
import { formatShortcut, getPrimaryModifierLabel } from '@/utils/keyboardShortcuts'

type GuideAction = 'table' | 'defaults' | 'relationship' | 'groups' | 'versions' | 'export' | 'import' | 'shortcuts'

interface GuideStep {
  id: string
  selector?: string
  icon: ReactNode
  accent: string
  action?: GuideAction
  shortcuts?: GuideShortcut[]
}

interface GuideShortcut {
  keys: string
  descriptionKey: string
}

interface HighlightRect {
  top: number
  left: number
  right: number
  bottom: number
  width: number
  height: number
}

function Backdrop({ rect }: { rect: HighlightRect | null }) {
  const shade = 'rgba(2, 6, 23, .72)'

  if (!rect) {
    return <div className="fixed inset-0" style={{ background: shade }} />
  }

  return (
    <>
      <div className="fixed left-0 right-0 top-0" style={{ height: rect.top, background: shade }} />
      <div className="fixed bottom-0 left-0 right-0" style={{ top: rect.bottom, background: shade }} />
      <div className="fixed left-0" style={{ top: rect.top, width: rect.left, height: rect.height, background: shade }} />
      <div className="fixed right-0" style={{ top: rect.top, left: rect.right, height: rect.height, background: shade }} />
      <div
        className="pointer-events-none fixed rounded-xl border-2"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          borderColor: 'var(--accent)',
          boxShadow: '0 0 0 4px var(--accent-glow), 0 0 28px var(--accent-glow)',
        }}
      />
    </>
  )
}

export function CanvasGuide() {
  const { t } = useTranslation()
  const open = useModalStore((state) => state.canvasGuideOpen)
  const closeGuide = useModalStore((state) => state.closeCanvasGuide)
  const openDefaults = useModalStore((state) => state.openDefaults)
  const openGroups = useModalStore((state) => state.openGroups)
  const openVersions = useModalStore((state) => state.openVersions)
  const openExport = useModalStore((state) => state.openExport)
  const openSqlImport = useModalStore((state) => state.openSqlImport)
  const openShortcuts = useModalStore((state) => state.openShortcuts)
  const openCreateTable = useTableEditorModal((state) => state.openCreate)
  const startRelationshipCreation = useSelectionStore((state) => state.startRelationshipCreation)
  const [stepIndex, setStepIndex] = useState(0)
  const [highlightRect, setHighlightRect] = useState<HighlightRect | null>(null)
  const modifierLabel = useMemo(getPrimaryModifierLabel, [])

  const steps = useMemo<GuideStep[]>(() => [
    { id: 'welcome', icon: <HelpIcon />, accent: '#4F6CF2' },
    { id: 'tabs', selector: '[data-guide="tabs"]', icon: <ProjectIcon />, accent: '#818CF8' },
    { id: 'sidebar', selector: '[data-guide="sidebar"]', icon: <TableIcon />, accent: '#64748B' },
    { id: 'navigate', selector: '[data-guide="canvas-modes"]', icon: <MoveIcon />, accent: '#38BDF8', shortcuts: [
      { keys: 'M', descriptionKey: 'shortcuts.moveMode' },
      { keys: 'S', descriptionKey: 'shortcuts.selectMode' },
    ] },
    { id: 'table', selector: '[data-guide="table-button"]', icon: <TableIcon />, accent: '#4F6CF2', action: 'table', shortcuts: [
      { keys: 'T', descriptionKey: 'shortcuts.createTableAtMouse' },
    ] },
    { id: 'relationship', selector: '[data-guide="relationship-button"]', icon: <RelationshipIcon />, accent: '#10C4B0', action: 'relationship', shortcuts: [
      { keys: 'R', descriptionKey: 'shortcuts.createRelationship' },
      { keys: 'Escape', descriptionKey: 'shortcuts.escape' },
    ] },
    { id: 'defaults', selector: '[data-guide="defaults-button"]', icon: <DefaultsIcon />, accent: '#F59E0B', action: 'defaults' },
    { id: 'groups', selector: '[data-guide="groups-button"]', icon: <GroupsIcon />, accent: '#A855F7', action: 'groups' },
    { id: 'versions', selector: '[data-guide="versions-button"]', icon: <HistoryIcon />, accent: '#EAB308', action: 'versions' },
    { id: 'export', selector: '[data-guide="export-button"]', icon: <ExportIcon />, accent: '#F97316', action: 'export', shortcuts: [
      { keys: 'Ctrl+E', descriptionKey: 'shortcuts.exportProject' },
    ] },
    { id: 'import', selector: '[data-guide="import-button"]', icon: <ImportIcon />, accent: '#06B6D4', action: 'import', shortcuts: [
      { keys: 'Ctrl+I', descriptionKey: 'shortcuts.importProject' },
    ] },
    { id: 'view', selector: '[data-guide="view-controls"]', icon: <AnimationIcon />, accent: '#14B8A6' },
    { id: 'shortcuts', selector: '[data-guide="shortcuts-button"]', icon: <ShortcutsIcon />, accent: '#EC4899', action: 'shortcuts', shortcuts: [
      { keys: 'Ctrl+Z', descriptionKey: 'shortcuts.undo' },
      { keys: 'Ctrl+C', descriptionKey: 'shortcuts.copyTable' },
      { keys: 'Ctrl+V', descriptionKey: 'shortcuts.pasteTable' },
      { keys: 'Delete', descriptionKey: 'shortcuts.deleteSelection' },
    ] },
    { id: 'zoom', selector: '[data-guide="zoom-controls"]', icon: <ResetZoomIcon />, accent: '#0EA5E9' },
    { id: 'finish', selector: '[data-guide="help-button"]', icon: <CheckIcon />, accent: '#10B981' },
  ], [])

  const step = steps[stepIndex]

  const close = useCallback(() => {
    markCanvasGuideSeen()
    closeGuide()
  }, [closeGuide])

  useEffect(() => {
    if (!open) return
    setStepIndex(0)
  }, [open])

  useEffect(() => {
    if (!open) return

    const updateHighlight = () => {
      if (!step.selector) {
        setHighlightRect(null)
        return
      }

      const target = document.querySelector<HTMLElement>(step.selector)
      if (!target) {
        setHighlightRect(null)
        return
      }

      const rect = target.getBoundingClientRect()
      const padding = 7
      setHighlightRect({
        top: Math.max(0, rect.top - padding),
        left: Math.max(0, rect.left - padding),
        right: Math.min(window.innerWidth, rect.right + padding),
        bottom: Math.min(window.innerHeight, rect.bottom + padding),
        width: Math.min(window.innerWidth, rect.right + padding) - Math.max(0, rect.left - padding),
        height: Math.min(window.innerHeight, rect.bottom + padding) - Math.max(0, rect.top - padding),
      })
    }

    updateHighlight()
    window.addEventListener('resize', updateHighlight)
    return () => window.removeEventListener('resize', updateHighlight)
  }, [open, step.selector])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
      if (event.key === 'ArrowRight' && stepIndex < steps.length - 1) setStepIndex((current) => current + 1)
      if (event.key === 'ArrowLeft' && stepIndex > 0) setStepIndex((current) => current - 1)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [close, open, stepIndex, steps.length])

  if (!open) return null

  const runAction = () => {
    if (!step.action) return
    close()
    if (step.action === 'table') openCreateTable()
    if (step.action === 'defaults') openDefaults()
    if (step.action === 'groups') openGroups()
    if (step.action === 'versions') openVersions()
    if (step.action === 'export') openExport()
    if (step.action === 'import') openSqlImport('editor-import')
    if (step.action === 'shortcuts') openShortcuts()
    if (step.action === 'relationship') {
      startRelationshipCreation()
      morphToast(t('relation.stepSource'))
    }
  }

  const isLastStep = stepIndex === steps.length - 1

  return (
    <div
      className="fixed inset-0 z-[1400] font-mono"
      role="dialog"
      aria-modal="true"
      aria-labelledby="canvas-guide-title"
      data-testid="canvas-guide"
    >
      <div className="absolute inset-0">
        <Backdrop rect={highlightRect} />
      </div>

      <section
        data-guide-panel="true"
        className="absolute bottom-5 right-5 flex max-h-[calc(100vh-40px)] w-[min(390px,calc(100vw-32px))] flex-col overflow-hidden rounded-2xl border shadow-2xl max-sm:bottom-4 max-sm:left-4 max-sm:right-4"
        style={{ background: 'var(--bg-modal)', borderColor: 'var(--border)', color: 'var(--text)' }}
      >
        <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${step.accent}, var(--teal))` }} />
        <div className="flex items-start justify-between gap-4 px-5 pb-3 pt-4">
          <div className="flex min-w-0 items-start gap-3">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
              style={{ color: step.accent, background: `${step.accent}1f` }}
            >
              {step.icon}
            </span>
            <div>
              <div className="mb-1 text-[9px] font-semibold uppercase tracking-[.18em]" style={{ color: step.accent }}>
                {t('canvasGuide.progress', { current: stepIndex + 1, total: steps.length })}
              </div>
              <h2 id="canvas-guide-title" className="text-sm font-semibold leading-5">
                {t(`canvasGuide.steps.${step.id}.title`)}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
            style={{ color: 'var(--text-muted)', background: 'var(--bg-card)' }}
            aria-label={t('common.close')}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="overflow-y-auto px-5 pb-4">
          <p className="text-[12px] leading-5" style={{ color: 'var(--text-dim)' }}>
            {t(`canvasGuide.steps.${step.id}.description`, { modifier: modifierLabel })}
          </p>
          <div className="mt-4 rounded-xl border p-3" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
            <div className="mb-2 text-[9px] font-semibold uppercase tracking-[.16em]" style={{ color: 'var(--text-muted)' }}>
              {t('canvasGuide.inPractice')}
            </div>
            <p className="text-[11px] leading-[1.65]" style={{ color: 'var(--text)' }}>
              {t(`canvasGuide.steps.${step.id}.tip`, { modifier: modifierLabel })}
            </p>
          </div>
          {step.shortcuts?.length ? (
            <div className="mt-3 space-y-1.5">
              <div className="mb-2 text-[9px] font-semibold uppercase tracking-[.14em]" style={{ color: 'var(--text-muted)' }}>
                {t('canvasGuide.shortcutsUsed')}
              </div>
              {step.shortcuts.map((shortcut) => (
                <div key={shortcut.keys} className="flex items-center gap-2 text-[10px]">
                  <kbd
                    className="min-w-[58px] rounded-md border px-2 py-1 text-center font-semibold"
                    style={{ borderColor: 'var(--border)', background: 'var(--bg-input)', color: 'var(--text)' }}
                  >
                    {formatShortcut([shortcut.keys], modifierLabel)[0]}
                  </kbd>
                  <span style={{ color: 'var(--text-dim)' }}>{t(shortcut.descriptionKey)}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="px-5 pb-2">
          <div className="flex gap-1" aria-hidden="true">
            {steps.map((item, index) => (
              <span
                key={item.id}
                className="h-1 flex-1 rounded-full transition-colors"
                style={{ background: index <= stepIndex ? step.accent : 'var(--border)' }}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t px-5 py-4" style={{ borderColor: 'var(--border)' }}>
          <Button variant="ghost" size="sm" onClick={close}>
            {t('canvasGuide.skip')}
          </Button>
          <div className="flex items-center gap-2">
            {step.action ? (
              <Button variant="secondary" size="sm" onClick={runAction}>
                {t(`canvasGuide.steps.${step.id}.action`)}
              </Button>
            ) : null}
            {stepIndex > 0 ? (
              <Button variant="secondary" size="sm" onClick={() => setStepIndex((current) => current - 1)}>
                {t('common.back')}
              </Button>
            ) : null}
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (isLastStep) close()
                else setStepIndex((current) => current + 1)
              }}
            >
              {isLastStep ? t('canvasGuide.finish') : t('canvasGuide.next')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}
